// client/src/utils/contrast.js
// WCAG contrast helpers for UI readability.

const clamp01 = (n) => Math.max(0, Math.min(1, n));

export const hexToRgb = (hex) => {
  if (!hex) return null;
  const cleaned = String(hex).trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  const num = parseInt(cleaned, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toLowerCase();

const srgbToLinear = (c) => {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (hexA, hexB) => {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  if (la == null || lb == null) return null;
  const L1 = Math.max(la, lb);
  const L2 = Math.min(la, lb);
  return (L1 + 0.05) / (L2 + 0.05);
};

const mixHex = (fgHex, bgHex, t) => {
  const fg = hexToRgb(fgHex);
  const bg = hexToRgb(bgHex);
  if (!fg || !bg) return null;
  const tt = clamp01(t);
  return rgbToHex({
    r: Math.round(fg.r * (1 - tt) + bg.r * tt),
    g: Math.round(fg.g * (1 - tt) + bg.g * tt),
    b: Math.round(fg.b * (1 - tt) + bg.b * tt)
  });
};

export const pickReadableTextColors = (backgroundHex, { minContrast = 4.5 } = {}) => {
  const bg = backgroundHex || '#ffffff';
  const black = '#000000';
  const white = '#ffffff';
  const blackRatio = contrastRatio(bg, black) || 0;
  const whiteRatio = contrastRatio(bg, white) || 0;
  const primaryText = blackRatio >= whiteRatio ? black : white;

  let secondaryText = primaryText;
  const bestRatio = contrastRatio(bg, primaryText) || 0;
  if (bestRatio >= minContrast) {
    let lo = 0;
    let hi = 0.7;
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      const candidate = mixHex(primaryText, bg, mid);
      const ratio = candidate ? (contrastRatio(bg, candidate) || 0) : 0;
      if (ratio >= minContrast) {
        lo = mid;
        secondaryText = candidate;
      } else {
        hi = mid;
      }
    }
  }

  return { primaryText, secondaryText };
};

